import { toast } from "react-toastify";
import { setTitle } from "../../../../helpers/MetaTag";
import { handleFilterChange } from "../../../../helpers";
import DeleteModal from "../../../../components/common/DeleteModal";

import { useDebounce } from "../../../../hooks/useDebounce";
import LimitSelect from "../../../../components/elements/LimitSelect";
import { useState } from "react";
import Search from "../../../../components/form/Search";
import Table from "../../../../components/elements/table/Table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Button from "../../../../components/form/Button";
import { orderDelete, orderList, orderOrdering, orderStatusChange } from "../../../../services/orderService";
import ManageOrderDrawer from "../../../../components/sections/admin/Order/Drawer/ManageOrderDrawer";
import dayjs from "dayjs";
import ActionBar from "../../../../components/elements/ActionBar";
import { lngDetect } from "../../../../helpers/lngDetect";
import Badge from "../../../../components/elements/Badge";
import { tableFilter } from "../../../../helpers/tableFilterOptions";
import { userList } from "../../../../services/userService";
import DatePicker from "../../../../components/form/DatePicker";
import { eventList } from "../../../../services/eventService";
import Select from "../../../../components/form/select/Select";
import PopoverDropdown from "../../../../components/elements/PopoverDropdown";

const Order = () => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const [userkeyword, setuserKeyword] = useState("");
  const usersearch = useDebounce(userkeyword, 300);
  const queryClient = useQueryClient();
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });
  const [userfilters, setuserFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "ordering",
  });
  const [eventFilters, setEventFilters] = useState({
    limit: 500,
    page: 1,
    sortQuery: "ordering",
  });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["order", filters, search],
    queryFn: async () => {
      const res = await orderList({
        ...filters,
        ...(search && { keyword: search }),
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  const statusList = [
    { name: "processing", value: "processing", type: "checkbox" },
    { name: "canceled", value: "canceled", type: "checkbox" },
    { name: "confirmed", value: "confirmed", type: "checkbox" },
    { name: "partial-refund", value: "partial-refund", type: "checkbox" },
    { name: "cancel-request", value: "cancel-request", type: "checkbox" },
  ];

  const {
    data: eventData,
    isLoading: eventLoading,
    isFetching: eventFetching,
  } = useQuery({
    queryKey: ["Event", eventFilters],
    queryFn: async () => {
      const queryParams = {
        ...eventFilters,
      };

      const res = await eventList(queryParams);
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs || {};
    },
  });

  //   Order status change handler =================>
  // const { mutate: statusChangeHandler, isPending: statusChangeInPending } = useMutation({
  //   mutationFn: async ({ id, status }) => {
  //     queryClient.setQueryData(["order", filters, search], (state) => {
  //       if (state) {
  //         let stateData = JSON.parse(JSON.stringify(state?.data));
  //         stateData = (stateData || []).map((elm) =>
  //           elm?._id === id
  //             ? {
  //                 ...elm,
  //                 status: elm?.status,
  //               }
  //             : elm
  //         );
  //         return { ...state, data: stateData };
  //       }
  //     });
  //     const res = await orderStatusChange({
  //       ids: id,
  //       status: status,
  //     });
  //     return { res, payload: { id, status } };
  //   },
  //   onSuccess: ({ res, payload }) => {
  //     if (res?.status !== 200) {
  //       queryClient.setQueryData(["order", filters, search], (state) => {
  //         if (state) {
  //           let stateData = JSON.parse(JSON.stringify(state?.data));
  //           stateData = (stateData || []).map((elm) =>
  //             elm?._id === payload?.id
  //               ? {
  //                   ...elm,
  //                   status: elm?.status,
  //                 }
  //               : elm
  //           );
  //           return { ...state, data: stateData };
  //         }
  //       });
  //     }
  //   },
  //   onError: (error) => {
  //     console.log(error);
  //   },
  // });

  const { mutate: statusChangeHandler } = useMutation({
    mutationFn: async ({ id, status }) => {
      const res = await orderStatusChange({
        ids: id,
        status: status,
      });
      return { res, payload: { id, status } };
    },

    // Optimistic Update
    onMutate: async ({ id, status }) => {
      await queryClient.cancelQueries(["order", filters, search]);

      const previousData = queryClient.getQueryData(["order", filters, search]);

      queryClient.setQueryData(["order", filters, search], (old) => {
        if (!old) return old;
        const newData = old.data.map((item) => (item._id === id ? { ...item, status: { info: status } } : item));
        return { ...old, data: newData };
      });

      // return rollback data
      return { previousData };
    },

    // If error, rollback
    onError: (err, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(["order", filters, search], context.previousData);
      }
      toast.error("Failed to update status");
    },

    // On success, ensure data is synced
    onSuccess: ({ res }) => {
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
    },

    // Refetch after success for fresh data (optional)
    onSettled: () => {
      queryClient.invalidateQueries(["order", filters, search]);
    },
  });

  const {
    data: userData,
    isFetching: userFetching,
    isLoading: userLoading,
    error: userError,
  } = useQuery({
    queryKey: ["user", usersearch, userfilters],
    queryFn: async () => {
      const queryParams = {
        ...userfilters,
        ...(usersearch && { keyword: usersearch }),
      };

      const res = await userList(queryParams);
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs || {};
    },
  });

  const tableHeadData = [
    {
      _id: "OrderId",
      name: "Order Id",
      align: "left",
    },
    {
      _id: 1,
      name: "Event Name",
      align: "left",
      ...tableFilter({
        filterKey: "event",
        filters,
        setFilters: (val) => {
          setFilters((pre) => ({ ...pre, ...val }));
        },
        filterList: eventData?.data?.map((item) => ({
          name: lngDetect(item?.title),
          value: item?._id,
          type: "checkbox",
        })),
      }),
    },
    {
      _id: 2,
      name: "Ticket Price",
      align: "left",
    },
    {
      _id: 3,
      name: "Order date",
      align: "left",
      // ...tableFilter({
      //   content: (
      //     <DatePicker
      //       onChange={({ startDate, endDate }) => {
      //         if (!startDate && !endDate) {
      //           setFilters((pre) => {
      //             delete pre?.startDate;
      //             delete pre?.endDate;
      //             return { ...pre };
      //           });
      //         } else {
      //           setFilters((pre) => ({ ...pre, startDate, endDate }));
      //         }
      //       }}
      //       renge
      //     />
      //   ),
      // }),

      ...tableFilter({
        content: (
          <DatePicker
            range
            onChange={({ startDate, endDate }) => {
              if (!startDate && !endDate) {
                setFilters((pre) => {
                  delete pre?.startDate;
                  delete pre?.endDate;
                  return { ...pre };
                });
              } else {
                setFilters((pre) => ({ ...pre, startDate, endDate }));
              }
            }}
          />
        ),
      }),
    },
    {
      _id: 4,
      name: "Order by",
      align: "left",
      ...tableFilter({
        filterKey: "user",
        filters,
        setFilters: (val) => {
          setFilters((pre) => ({ ...pre, ...val }));
        },
        filterList: userData?.data?.map((item) => ({
          name: `${item?.firstName} ${item?.lastName}`,
          value: item?._id,
          type: "checkbox",
        })),
      }),
    },
    {
      _id: 5,
      name: "Status",
      align: "left",
      ...tableFilter({
        filterKey: "status",
        filters,
        setFilters: (val) => {
          setFilters((pre) => ({ ...pre, ...val }));
        },
        filterList: [
          { name: "processing", value: "processing", type: "checkbox" },
          { name: "canceled", value: "canceled", type: "checkbox" },
          { name: "confirmed", value: "confirmed", type: "checkbox" },
          { name: "partial-refund", value: "partial-refund", type: "checkbox" },
          { name: "cancel-request", value: "cancel-request", type: "checkbox" },
        ],
      }),
    },
    {
      _id: "paymentStatus",
      name: "payment Status",
      align: "left",
      ...tableFilter({
        filterKey: "paymentStatus",
        filters,
        setFilters: (val) => {
          setFilters((pre) => ({ ...pre, ...val }));
        },
        filterList: [
          { name: "pending", value: "pending", type: "checkbox" },
          { name: "success", value: "success", type: "checkbox" },
        ],
      }),
    },
    // {
    //   _id: 6,
    //   name: "Created",
    //   align: "left",
    // },
    {
      _id: 8,
      name: "Action",
      align: "left",
    },
  ];

  // Table Body Data =====================>
  const tableData = data?.data?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.orderId || "-",
        },
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: lngDetect(item?.eventData?.title) || "-",
        },
        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (item?.eventData?.currency?.symbol ? "(" + item?.eventData?.currency?.symbol + ")" : "") + " " + (item?.total || "-"),
        },
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: dayjs(item?.createdAt).format("MMM DD, YYYY") || "-",
        },
        {
          _id: 5,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.userData?.firstName || item?.userData?.lastName ? `${item?.userData?.firstName} ${item?.userData?.lastName}` : "-",
        },
        {
          _id: 6,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <div>
              <PopoverDropdown
                buttonClass={"p-0 bg-transparent"}
                buttonLabel={
                  <Badge label={item?.status?.info} className={"capitalize"} type={item?.status?.info === "confirmed" ? "success" : "danger"} />
                }
              >
                <ul>
                  {statusList?.map((status, i) => (
                    <li key={i?.toString()} className="hover:text-white px-2 py-2 hover:bg-gray-700/40 rounded-md">
                      <button
                        className="flex items-center justify-start w-full h-full"
                        type="button"
                        onClick={() => {
                          statusChangeHandler({
                            status: status?.value,
                            id: item?._id,
                          });
                        }}
                      >
                        {status?.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </PopoverDropdown>
            </div>
          ),
        },
        {
          _id: "paymentStatus",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.paymentStatus,
        },
        // {
        //   _id: 3,
        //   type: "text",
        //   align: "left",
        //   cellClass: "!min-w-[100px]",
        //   data: dayjs(item?.createdAt).format("MMM DD, YYYY") || "-",
        // },
        {
          _id: "action",
          type: "action",
          align: "left",
          cellClass: "w-[5%]",
          actionData: [
            {
              _id: "edit",
              name: "Details",
              icon: "fa-regular fa-circle-info",
              onClick: () => {
                setModalOpen({ isOpen: true, data: item });
              },
              standout: false,
            },
            // {
            //   _id: "delete",
            //   name: "Delete",
            //   icon: "fa-regular fa-trash-can",
            //   standout: true,
            //   onClick: () => {
            //     setDeleteModal({
            //       isOpen: true,
            //       data: item,
            //     });
            //   },
            // },
          ],
        },
      ],
    };
  });

  // add or edit page =================>
  function pageManageResponse(res) {
    if (res?.status === 200 || res?.status === 201) {
      if (res?.status === 201) {
        if (filters?.page === 1) {
          queryClient.setQueryData(["order", filters, search], (state) => {
            if (state) {
              let stateData = JSON.parse(JSON.stringify(state?.data));
              stateData = [...stateData, res?.data];
              return { ...state, data: stateData };
            }
          });
          if (data?.metadata?.totalDocs > filters?.limit) {
            queryClient.invalidateQueries(["order", filters, search]);
          }
        } else {
          setFilters((pre) => ({ ...pre, page: 1 }));
        }
      }
      if (res?.status === 200) {
        queryClient.setQueryData(["order", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = stateData?.map((item) => (item?._id === res?.data?._id ? { ...item, ...res?.data } : item));
            return { ...state, data: stateData };
          }
        });
        if (data?.metadata?.totalDocs > filters?.limit) {
          queryClient.invalidateQueries(["order", filters, search]);
        }
      }
      setModalOpen({ isOpen: false, data: null });
    } else {
      toast.warning(res?.message || res?.error);
    }
  }

  //   Order delete handler =================>
  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      const res = await orderDelete({
        ids: Array.isArray(payload) ? payload : [payload],
      });
      return {
        res,
        payload: Array.isArray(payload) ? payload : [payload],
      };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        setDeleteModal({ isOpen: false, data: null });
        queryClient.setQueryData(["order", filters, search], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = stateData?.filter((item) => !payload?.includes(item?._id));

            return { ...state, data: stateData };
          }
        });

        if (data?.metadata?.totalDocs > filters?.limit) {
          queryClient.invalidateQueries(["order", filters, search]);
        }
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const options = [
    {
      id: "userDelete",
      icon: "fa-regular fa-trash-can",
      tooltip: "User Delete",
    },
  ];

  function actionHandler(val) {
    switch (val) {
      case "userDelete":
        setDeleteModal({
          isOpen: true,
          data: { _id: selectedItems },
        });
        break;

      default:
        break;
    }
  }

  const { mutate: handleOrdering, isPending } = useMutation({
    mutationFn: async ({ newData, oldData }) => {
      const res = await orderOrdering({
        ordering_data: newData?.map((item, i) => ({
          id: item?._id,
          ordering: i,
        })),
      });
      return { res, newData, oldData };
    },
    onSuccess: ({ res, newData, oldData }) => {
      if (res?.status !== 200) {
        queryClient.setQueryData(["order", filters, search], (state) => {
          if (state) {
            return { ...state, data: oldData };
          }
        });
        toast.warning(res?.message || res?.error);
      }
    },
  });

  return (
    <div className="w-full flex flex-col pb-10 bg-white rounded-md">
      <div className="space-y-8 py-8 px-5">
        <div className="flex justify-between items-center border-b border-gray-300">
          <div className="flex flex-col gap-2 w-full">
            <div className="font-bold text-lg xl:text-2xl uppercase tracking-tighter font-Montserrat">Order Management</div>
            <div className="text-sm xl:text-base h-10">Easily handle all order actions: update order info, status, and add new order data.</div>
          </div>
          {/* <div className="relative">
            <Button
              buttonClasses={
                "!bg-transparent border border-transparent !h-10 hover:!bg-primary/10 hover:!border-primary/10"
              }
              variant="secondary"
              buttonLabel={"Create New"}
              buttonLabelClasses={
                "font-medium !text-sm uppercase whitespace-nowrap"
              }
              buttonHasLink
              buttonFunction={() => setModalOpen({ isOpen: true, data: null })}
            />
          </div> */}
        </div>
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-full md:w-36">
              <LimitSelect
                value={filters?.limit}
                allValue={data?.metadata?.totalDocs}
                onChange={(val) => handleFilterChange("limit", val, setFilters)}
              />
            </div>
            <div className="w-full md:w-72">
              <Search
                placeholder={"Search here"}
                // divClasses={"!border-slate-300 !bg-slate-50"}
                search={(val) => {
                  setKeyword(val);
                  handleFilterChange("page", 1, setFilters);
                }}
              />
            </div>
            {selectedItems?.length > 0 && (
              <div className="flex items-center gap-2">
                <ActionBar options={options} onChange={actionHandler} />
                <span className="flex items-center rounded-full bg-secondary text-surface-01 text-xs px-3 py-1 gap-2">
                  {selectedItems?.length} Selected
                  <button onClick={() => setSelectedItems([])} type="button" className="cursor-pointer">
                    <i className="fa-fw fa-light fa-xmark"></i>
                  </button>
                </span>
              </div>
            )}
          </div>
          {/* <div className="flex items-center gap-3">
            <div className="relative">
              <Select
                selectedValue={"Buyer"}
                dropdownData={[{ name: "Buyer", value: "Buyer" }]}
                onChange={(val) => {
                  console.log({ val });
                  handleChange({ target: { name: "parent", value: val } });
                }}
                onScroll={(e) => {
                  loadData(e, categories, setCategoryilters);
                }}
              />
            </div>
            <div className="relative">
              <Select
                selectedValue={"Past 3 month"}
                dropdownData={[{ name: "Past 3 month", value: "Past 3 month" }]}
                // onChange={(val) => {
                //   console.log({ val });
                //   handleChange({ target: { name: "parent", value: val } });
                // }}
                // onScroll={(e) => {
                //   loadData(e, categories, setCategoryilters);
                // }}
              />
            </div>
          </div> */}
        </div>
        <Table
          tableHeadData={tableHeadData}
          tableData={tableData}
          isLoder={isLoading}
          pageCount={data?.metadata?.totalPages}
          currentPage={filters?.page}
          onPageChange={(val) => {
            handleFilterChange("page", val, setFilters);
            setSelectedItems([]);
          }}
          originalData={[...(data?.data || [])]}
          setOriginalData={(val) => {
            queryClient.setQueryData(["order", filters, search], (state) => {
              if (state) {
                return { ...state, data: val };
              }
            });
          }}
          handleOrdering={(newData, oldData) => handleOrdering({ newData, oldData })}
          isOrderingDisabled={false}
          isFetching={isFetching}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          tableActionDropdownContainer={"!w-48"}
          // selectable
        />
      </div>
      <ManageOrderDrawer
        open={modalOpen?.isOpen}
        data={modalOpen?.data}
        close={() => setModalOpen({ isOpen: false, data: null })}
        getResponse={pageManageResponse}
      />
      <DeleteModal
        open={deleteModal?.isOpen}
        loading={deletePending}
        onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
        deleteHandler={() => {
          deleteHandler(deleteModal?.data?._id);
        }}
      />
    </div>
  );
};

export default Order;
